import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { notifyWorker } from '../../lib/notifyWorker'

function fmtDate(s) {
  if (!s) return ''
  const d = new Date(s + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' })
}

export default function FindReplacementModal({ request, onClose }) {
  const { profile, user } = useAuth()
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(true)
  const [askedIds, setAskedIds] = useState(new Set())
  const [jobLabel, setJobLabel] = useState('')

  const load = useCallback(async () => {
    if (!request) return
    setLoading(true)

    const today = request.request_date
    // Compute week bounds
    const d = new Date(today + 'T00:00:00')
    const dow = d.getDay()
    const weekStartD = new Date(d)
    weekStartD.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow))
    const weekStart = weekStartD.toISOString().slice(0, 10)
    const weekEndD = new Date(weekStartD)
    weekEndD.setDate(weekStartD.getDate() + 5)
    const weekEnd = weekEndD.toISOString().slice(0, 10)

    // All in-season workers
    const { data: pool } = await supabase.from('profiles')
      .select('id, full_name, role, seniority_rank, max_days_per_week, season_start, season_end, availability_status, phone')
      .neq('role', 'store_partner')
      .eq('is_active', true)
      .eq('availability_status', 'available')

    // Busy today: scheduled, called_off, or NCNS
    const { data: schedToday } = await supabase.from('worker_schedule')
      .select('user_id, status')
      .eq('schedule_date', today)
    const busy = new Set((schedToday || [])
      .filter((r) => ['scheduled', 'called_off', 'no_call_no_show'].includes(r.status))
      .map((r) => r.user_id))

    // Week count
    const { data: weekRows } = await supabase.from('worker_schedule')
      .select('user_id, status')
      .gte('schedule_date', weekStart)
      .lte('schedule_date', weekEnd)
    const weekCount = {}
    for (const r of (weekRows || [])) {
      if (r.status === 'scheduled') weekCount[r.user_id] = (weekCount[r.user_id] || 0) + 1
    }

    // Already offered
    const { data: offers } = await supabase.from('replacement_offers')
      .select('offered_to, status')
      .eq('day_off_request_id', request.id)
    const offerStatus = {}
    for (const o of (offers || [])) offerStatus[o.offered_to] = o.status

    // Build candidate list
    const me = user.id
    const list = (pool || [])
      .filter((w) => w.id !== me)
      .filter((w) => !busy.has(w.id))
      .filter((w) => (weekCount[w.id] || 0) < (w.max_days_per_week || 6))
      .map((w) => ({
        ...w,
        days_this_week: weekCount[w.id] || 0,
        offer_status: offerStatus[w.id] || null,
      }))
      .sort((a, b) => {
        if (a.days_this_week !== b.days_this_week) return a.days_this_week - b.days_this_week
        const ra = a.seniority_rank || 999, rb = b.seniority_rank || 999
        return rb - ra // most junior first
      })

    setCandidates(list)
    setAskedIds(new Set(Object.keys(offerStatus).filter((id) => offerStatus[id] !== 'declined')))

    // Look up job label if possible
    const { data: mySched } = await supabase.from('worker_schedule')
      .select('job_id')
      .eq('user_id', me)
      .eq('schedule_date', today)
      .maybeSingle()
    if (mySched?.job_id) {
      const { data: job } = await supabase.from('jobs')
        .select('title, site_address, client_name').eq('id', mySched.job_id).maybeSingle()
      if (job) setJobLabel((job.title || job.client_name || '') + (job.site_address ? ' · ' + job.site_address : ''))
    }

    setLoading(false)
  }, [request, user])

  useEffect(() => { load() }, [load])

  const ask = async (candidate) => {
    setAskedIds(new Set([...askedIds, candidate.id]))
    // Create offer (capture the id so the notification deep-links to it)
    const { data: offerRow } = await supabase.from('replacement_offers').insert({
      day_off_request_id: request.id,
      offered_to: candidate.id,
      status: 'pending',
    }).select().single()
    // Mark request replacement_status as pending if still none
    await supabase.from('day_off_requests').update({ replacement_status: 'pending' })
      .eq('id', request.id)

    await notifyWorker({
      userId: candidate.id,
      type: 'replacement_offer',
      title: 'Cover for ' + (profile?.full_name || 'a teammate') + '?',
      message: fmtDate(request.request_date) + (jobLabel ? ' · ' + jobLabel : ''),
      link: '/schedule?offer=' + (offerRow?.id || ''),
      relatedId: offerRow?.id || request.id,
    })
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.title}>FIND A REPLACEMENT</div>
        <div style={styles.sub}>{fmtDate(request.request_date)}{jobLabel ? ' · ' + jobLabel : ''}</div>
        <div style={styles.list}>
          {loading && <div style={styles.empty}>Loading...</div>}
          {!loading && candidates.length === 0 && (
            <div style={styles.empty}>No eligible crew available that day.</div>
          )}
          {candidates.map((c, i) => {
            const asked = askedIds.has(c.id)
            return (
              <div key={c.id} style={styles.row}>
                <div style={styles.rowMain}>
                  <div style={styles.rowName}>
                    {c.full_name}
                    {i === 0 && !asked && <span style={styles.recTag}>RECOMMENDED</span>}
                    {c.offer_status === 'declined' && <span style={styles.declinedTag}>DECLINED</span>}
                  </div>
                  <div style={styles.rowMeta}>
                    {(c.role || '').replace('_', ' ')} · {c.days_this_week} day{c.days_this_week === 1 ? '' : 's'} this week
                  </div>
                </div>
                <button
                  onClick={() => ask(c)}
                  disabled={asked}
                  style={{ ...styles.askBtn, ...(asked ? styles.askedBtn : {}) }}
                >
                  {asked ? 'ASKED' : 'ASK'}
                </button>
              </div>
            )
          })}
        </div>
        <div style={styles.actions}>
          <button onClick={onClose} style={styles.cancelBtn}>DONE</button>
        </div>
      </div>
    </div>
  )
}

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, fontFamily: "'Barlow', sans-serif" },
  modal: { backgroundColor: '#0d1420', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '6px', width: 'min(92vw, 520px)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '3px', color: '#b48c3c', padding: '18px 20px 6px' },
  sub: { fontSize: '13px', color: 'rgba(255,255,255,0.45)', padding: '0 20px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  list: { padding: '12px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px' },
  rowMain: { flex: 1 },
  rowName: { fontSize: '14px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  rowMeta: { fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' },
  recTag: { backgroundColor: '#4a8c6a', color: '#0a0f1a', padding: '1px 6px', borderRadius: '2px', fontSize: '10px', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '1px', fontWeight: 700 },
  declinedTag: { backgroundColor: 'rgba(224,92,92,0.15)', color: '#e05c5c', padding: '1px 6px', borderRadius: '2px', fontSize: '10px', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '1px' },
  askBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '8px 14px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  askedBtn: { backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', cursor: 'not-allowed' },
  actions: { padding: '12px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'flex-end' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '8px 14px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', letterSpacing: '2px', cursor: 'pointer' },
  empty: { padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px' },
}
